import { ExperimentalEntityTile, ExperimentalEntityTileGroup, Stacked, Typography } from '@statsbomb/kitbag-components';
import { PlayerAvatar, TeamAvatar } from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { secureBucketCookieRequestedAtom } from '@/atoms/cookies';
import { recentlyVisitedAtom } from '@/atoms/home/recentlyVisited';
import { useUrlLocale } from '@/hooks/useUrlLocale';
import { RecentVisit } from '@/types/recentlyVisited';

const RecentVisitAvatar = ({ visit, canRequestImage }: { visit: RecentVisit; canRequestImage: boolean }) => {
  const id = canRequestImage ? visit.id : undefined;

  return visit.type === 'player' ? (
    <PlayerAvatar playerId={id} playerName={visit.name} size="medium" />
  ) : (
    <TeamAvatar teamId={id} size="medium" />
  );
};

export const RecentlyVisited = () => {
  const { t } = useTranslation('home');
  const headingId = useId();
  const locale = useUrlLocale();
  const visits = useAtomValue(recentlyVisitedAtom);
  // Avatars only request images once the secure image bucket cookie is in place, as in EntityTopBar.
  const secureBucketCookieRequested = useAtomValue(secureBucketCookieRequestedAtom);

  if (!visits.length) return null;

  return (
    <Stacked direction="column" gap="small">
      <Stacked direction="column" alignItems="center">
        <Typography as="h2" variant="bodySmall" muted id={headingId}>
          {t('recentlyVisited.title')}
        </Typography>
      </Stacked>
      <ExperimentalEntityTileGroup align="center" ariaLabelledBy={headingId}>
        {visits.map(visit => (
          <ExperimentalEntityTile
            key={`${visit.type}-${visit.id}`}
            avatar={<RecentVisitAvatar visit={visit} canRequestImage={secureBucketCookieRequested} />}
            title={visit.name}
            subtitle={visit.subline}
            component={Link}
            componentProps={{ to: `/${locale}/${visit.type}/${visit.id}/dashboard` }}
            testId={`recently-visited-${visit.type}-${visit.id}`}
          />
        ))}
      </ExperimentalEntityTileGroup>
    </Stacked>
  );
};
